// The marks on a review's progress bar for the review session under way, and
// the one the learner has just crossed (see reviewProgressMarkRules.ts).
//
// An answer that crosses a mark opens nothing by itself: the learner still
// wants to read what they got wrong and may ask for an explanation (owner,
// 2026-10-05). The crossing is remembered here, the button under the answer
// reads "next" instead of "next question", and pressing it opens the station
// or the voice check.
//
// Imported by code that runs at app startup (the answer-saving helpers and the
// review's navigation). Keep it small.

import { create } from "zustand";
import { calculateReviewProgressPercentage } from "../backend/utils/review-progress-helpers";
import {
  getReviewProgressMarksCrossed,
  type ReviewProgressMark,
  type ReviewProgressMarkKind,
} from "../review-progress-marks/reviewProgressMarkRules";

interface ReviewProgressMarksStoreState {
  // Worked out by the review page; empty outside a review and for a learner
  // who gets no marks.
  marks: ReviewProgressMark[];
  setMarks: (marks: ReviewProgressMark[]) => void;

  // The kinds whose mark an answer has crossed in this review session. Each
  // kind opens at most once per session, so a mark crossed again after
  // progress fell does nothing.
  markKindsCrossedInThisReviewSession: ReviewProgressMarkKind[];
  // Crossed, and waiting for the learner to press "next".
  pendingMarkKind: ReviewProgressMarkKind | null;
  noteMarksCrossed: (marksCrossed: ReviewProgressMark[]) => void;
  // Hands over the pending mark and forgets it.
  takePendingMarkKind: () => ReviewProgressMarkKind | null;
  // A kind that opened without a mark (the voice check at the end of the first review,
  // firstExperienceGelRules.ts): counted as crossed, so the rest of the set does not open it again, and the
  // rest of the set's bar does not draw its mark (useReviewProgressMarks).
  markKindsOpenedWithoutTheirMarkInThisReviewSession: ReviewProgressMarkKind[];
  noteMarkKindOpenedWithoutItsMark: (markKind: ReviewProgressMarkKind) => void;

  startNewReviewSession: () => void;
}

export const useReviewProgressMarksStore = create<ReviewProgressMarksStoreState>((set, get) => ({
  marks: [],
  setMarks: (marks) => set({ marks }),

  markKindsCrossedInThisReviewSession: [],
  pendingMarkKind: null,
  noteMarksCrossed: (marksCrossed) => {
    const { markKindsCrossedInThisReviewSession, pendingMarkKind } = get();
    const newlyCrossedMarks = marksCrossed.filter((mark) => !markKindsCrossedInThisReviewSession.includes(mark.kind));
    if (newlyCrossedMarks.length === 0) return;
    set({
      markKindsCrossedInThisReviewSession: [
        ...markKindsCrossedInThisReviewSession,
        ...newlyCrossedMarks.map((mark) => mark.kind),
      ],
      // One answer on a very short set can cross both marks. Only the first
      // opens: nobody is sent through a station and a voice check back to back.
      pendingMarkKind: pendingMarkKind ?? newlyCrossedMarks[0].kind,
    });
  },
  takePendingMarkKind: () => {
    const { pendingMarkKind } = get();
    if (pendingMarkKind !== null) set({ pendingMarkKind: null });
    return pendingMarkKind;
  },

  markKindsOpenedWithoutTheirMarkInThisReviewSession: [],
  noteMarkKindOpenedWithoutItsMark: (markKind) => {
    const { markKindsCrossedInThisReviewSession, markKindsOpenedWithoutTheirMarkInThisReviewSession } = get();
    if (markKindsCrossedInThisReviewSession.includes(markKind)) return;
    set({
      markKindsCrossedInThisReviewSession: [...markKindsCrossedInThisReviewSession, markKind],
      markKindsOpenedWithoutTheirMarkInThisReviewSession: [
        ...markKindsOpenedWithoutTheirMarkInThisReviewSession,
        markKind,
      ],
    });
  },

  startNewReviewSession: () =>
    set({
      marks: [],
      markKindsCrossedInThisReviewSession: [],
      markKindsOpenedWithoutTheirMarkInThisReviewSession: [],
      pendingMarkKind: null,
    }),
}));

// Called where an answer changes the review's progress score.
export function noteReviewProgressChangedByAnswer({
  previousDeckProgressScore,
  newDeckProgressScore,
  maxDeckProgressScore,
}: {
  previousDeckProgressScore: number;
  newDeckProgressScore: number;
  maxDeckProgressScore: number;
}): void {
  const { marks, noteMarksCrossed } = useReviewProgressMarksStore.getState();
  if (marks.length === 0) return;
  noteMarksCrossed(
    getReviewProgressMarksCrossed({
      marks,
      previousProgressPercentage: calculateReviewProgressPercentage(previousDeckProgressScore, maxDeckProgressScore),
      newProgressPercentage: calculateReviewProgressPercentage(newDeckProgressScore, maxDeckProgressScore),
    })
  );
}

export function useIsReviewProgressMarkWaitingForNext(): boolean {
  return useReviewProgressMarksStore((state) => state.pendingMarkKind !== null);
}
